import { HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { DraggablePanel } from '@components/ui/draggable';
import { useAppSelector } from '@hooks/redux.hooks';
import useListFormatter from '@hooks/useListFormatter';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { selectActiveOrganization } from '@redux/authent/authent.selectors';
import { selectGroupedTargetWithSelectedTarget, selectStrobeById } from '@redux/situation/situation.selectors';
import { formatDate } from '@utils/date.utils';
import { getFrequencyFromRfData, getStrobeIcon } from '@utils/map/strobe.utils';
import { getTargetDisplayedId, getTargetIdentification } from '@utils/target.utils';
import { FormattedMessage, useIntl } from 'react-intl';

import { Target } from '@/types/c2/c2.types';
import { DateTimeEnum } from '@/types/dateTime.types';
import { PopupPositionData, StrobeInfoData } from '@/types/popup.types';
import { StrobeLocation } from '@/types/sensor/sensor.types';

import InfoValue from './InfoValue';

function StrobeInfo({ mapConstraintsRef, targetId, top, left, onClose }: Readonly<StrobeInfoData & PopupPositionData>) {
  const { formatMessage } = useIntl();
  const formatList = useListFormatter();

  const target: Target<StrobeLocation> | undefined = useSelectorWithReplayMode(selectStrobeById, targetId);
  const groupedTarget = useSelectorWithReplayMode(selectGroupedTargetWithSelectedTarget, target);
  const organization = useAppSelector(selectActiveOrganization);

  if (!target) {
    onClose();
    return null;
  }
  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={top} left={left} offsetY={-133}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="strobeInfos.strobeInfos" />
      </DraggablePanel.Header>
      <DraggablePanel.Body height={608}>
        <HStack alignItems="start" backgroundColor="neutral.700" width="442" minHeight="100%" gap={0.25}>
          <VStack alignItems="start" flexGrow={1} gap={4} marginLeft={4} marginTop={4}>
            <InfoValue
              label={formatMessage({ id: 'strobeInfos.type' })}
              value={formatMessage({ id: `target.identification.${getTargetIdentification(target, organization)}` })}
            />
            <HStack flexGrow={1} gap={4}>
              <InfoValue
                label={formatMessage({ id: 'strobeInfos.frequency' })}
                value={getFrequencyFromRfData(target.lastPosition?.rfData) ?? 'N/A'}
              />
              <InfoValue
                label={formatMessage({ id: 'strobeInfos.protocol' })}
                value={target.lastPosition?.rfData?.protocol ?? 'N/A'}
              />
              <InfoValue
                label={formatMessage({ id: 'strobeInfos.rssi' })}
                value={target.lastPosition?.rfData?.rssi ?? 'N/A'}
              />
            </HStack>
            <InfoValue
              label={formatMessage({ id: 'strobeInfos.detector' })}
              value={formatList(target.trackIds.map((track) => track.sensorCode))}
            />
            <InfoValue
              label={formatMessage({ id: 'strobeInfos.droneType' })}
              value={target.lastPosition?.collaborativeData?.model ?? 'N/A'}
            />
            <VStack alignItems="start">
              <Text textStyle="md" fontWeight="medium" color="neutral.300">
                {formatMessage({ id: 'strobeInfos.detection' })}
              </Text>
              <HStack width="100%" gap={2} alignItems="start">
                <VStack alignItems="start" justifyContent="end">
                  <HStack width="100%" gap={1} alignItems="start">
                    <Text textStyle="md" fontWeight="medium" width="65px" color="neutral.300" lineClamp={1}>
                      {formatMessage({ id: 'strobeInfos.start' })}
                    </Text>
                    <Text textStyle="lg" fontWeight="medium">
                      {formatDate(target.detectionStart, DateTimeEnum.DATE_TIME)}
                    </Text>
                  </HStack>
                  <HStack width="100%" gap={1} alignItems="start">
                    <Text textStyle="md" fontWeight="medium" width="65px" color="neutral.300" lineClamp={1}>
                      {formatMessage({ id: 'strobeInfos.end' })}
                    </Text>
                    <Text textStyle="lg" fontWeight="medium">
                      {formatDate(target.detectionEnd, DateTimeEnum.DATE_TIME)}
                    </Text>
                  </HStack>

                  <HStack width="100%" gap={1} alignItems="start">
                    <Text textStyle="md" fontWeight="medium" width="65px" color="neutral.300" lineClamp={1}>
                      {formatMessage({ id: 'strobeInfos.azimuth' })}
                    </Text>
                    <Text textStyle="lg" fontWeight="medium" lineClamp={1}>
                      {target.lastPosition!.location!.azimuth!.toFixed(0)}°
                    </Text>
                  </HStack>
                </VStack>
                <VStack alignItems="start" justifyContent="end"></VStack>
              </HStack>
            </VStack>
            <InfoValue
              label={formatMessage({ id: 'strobeInfos.linkedTo' })}
              value={getTargetDisplayedId(groupedTarget)}
            />
          </VStack>
          <Icon as={getStrobeIcon(target, organization)} width="70px" height="70px" margin={4} />
        </HStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}

export default StrobeInfo;
